import React, { useState, useRef, useEffect } from "react";
import {
  Box, Typography, Grid, TextField, Paper, Checkbox,
  FormControlLabel, Button, IconButton, Avatar, Stack, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import {
  AddCircleOutline,
  DeleteOutline,
  CloudUpload as CloudUploadIcon,
  CameraAlt as CameraAltIcon,
  Close as CloseIcon
} from "@mui/icons-material";
import { storage } from "../../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

function GuardianInfo({ form, handleChange }) {
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const guardians = form.guardians || [];

  // Webcam States and Refs
  const [cameraOpen, setCameraOpen] = useState(false);
  const [stream, setStream] = useState(null);
  const [activeCameraIndex, setActiveCameraIndex] = useState(null); // Tracks which guardian is capturing
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // --- REUSABLE FIREBASE UPLOAD LOGIC ---
  const uploadPhotoToFirebase = async (fileBlob, fileName, index) => {
    setUploadingIndex(index);
    try {
      const storageRef = ref(storage, `guardian_photos/${Date.now()}_${fileName}`);
      await uploadBytes(storageRef, fileBlob);
      const downloadURL = await getDownloadURL(storageRef);

      // Update specific guardian's photo in the array
      const updatedList = [...guardians];
      updatedList[index] = { ...updatedList[index], photo: downloadURL };
      handleChange("guardians", updatedList);
    } catch (error) {
      console.error("Upload error:", error);
      alert("Failed to upload image. Please try again.");
    } finally {
      setUploadingIndex(null);
    }
  };

  // --- FILE UPLOAD HANDLER ---
  const handlePhotoUpload = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      uploadPhotoToFirebase(file, file.name, index);
    }
  };

  // --- WEBCAM LOGIC ---
  const startCamera = async (index) => {
    setActiveCameraIndex(index);
    setCameraOpen(true);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: true });
      setStream(mediaStream);
    } catch (error) {
      console.error("Camera access denied:", error);
      alert("Camera access denied or unavailable.");
      setCameraOpen(false);
    }
  };

  // Attach stream to video element when it becomes available
  useEffect(() => {
    if (cameraOpen && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [cameraOpen, stream]);

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setCameraOpen(false);
    setStream(null);
    setActiveCameraIndex(null);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current && activeCameraIndex !== null) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to Blob and upload
      canvas.toBlob((blob) => {
        if (blob) {
          const indexToUpdate = activeCameraIndex;
          stopCamera();
          uploadPhotoToFirebase(blob, `webcam_capture.jpg`, indexToUpdate);
        }
      }, "image/jpeg", 0.9);
    }
  };

  // Cleanup camera stream if component unmounts
  useEffect(() => {
    return () => stopCamera();
  }, []);

  // --- GUARDIAN ARRAY OPERATIONS ---
  const updateGuardian = (index, field, value) => {
    const updatedList = [...guardians];
    updatedList[index] = { ...updatedList[index], [field]: value };
    handleChange("guardians", updatedList);
  };

  const addGuardian = () => {
    const newG = { name: "", relationship: "", photo: "", mobile: "", email: "" };
    handleChange("guardians", [...guardians, newG]);
  };

  const removeGuardian = (index) => {
    const updatedList = guardians.filter((_, i) => i !== index);
    handleChange("guardians", updatedList);
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" bgcolor="#f4f6f8" p={2}>
      <Paper sx={{ p: 3, width: "100%", maxWidth: "1200px" }}>

        {/* HEADER */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ background: "teal", color: "#fff", p: 1, mb: 3, borderRadius: 1 }}>
          <Typography fontWeight="bold">5) Guardian's Information</Typography>
          <Button variant="contained" color="secondary" startIcon={<AddCircleOutline />} onClick={addGuardian}>
            Add Guardian
          </Button>
        </Stack>

        {guardians.map((guardian, index) => (
          <Box key={index} sx={{ mb: 2, p: 1, border: "2px dashed teal", borderRadius: 2, bgcolor: "#fff" }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h6" color="teal">Guardian #{index + 1}</Typography>
              <IconButton color="error" onClick={() => removeGuardian(index)}><DeleteOutline /></IconButton>
            </Stack>

            <Grid className="row" spacing={3}>
              {/* LEFT: FORM FIELDS */}
              <Grid className="col-sm-9 mb-1" item xs={12} md={9}>
                <Grid className="row" spacing={2}>
                  <Grid className="col-sm-4 mb-2" item xs={12} sm={8}>
                    <TextField fullWidth label="Name" size="small" value={guardian.name || ""} onChange={(e) => updateGuardian(index, "name", e.target.value)} />
                  </Grid>
                  <Grid className="col-sm-4 mb-2" item xs={12} sm={4}>
                    <TextField fullWidth label="Relationship" size="small" value={guardian.relationship || ""} onChange={(e) => updateGuardian(index, "relationship", e.target.value)} />
                  </Grid>
                  <Grid className="col-sm-4 mb-2" item xs={12} sm={9}>
                    <TextField fullWidth label="Residential Address" size="small" value={guardian.address || ""} onChange={(e) => updateGuardian(index, "address", e.target.value)} />
                  </Grid>
                  <Grid className="col-sm-4 mb-2" item xs={12} sm={3}>
                    <TextField fullWidth label="Pin" size="small" value={guardian.pin || ""} onChange={(e) => updateGuardian(index, "pin", e.target.value)} />
                  </Grid>
                  <Grid className="col-sm-4 mb-2" item xs={12} sm={6}>
                    <TextField fullWidth label="Email" size="small" value={guardian.email || ""} onChange={(e) => updateGuardian(index, "email", e.target.value)} />
                  </Grid>
                  <Grid className="col-sm-4 mb-2" item xs={12} sm={6}>
                    <TextField fullWidth label="Mobile" size="small" value={guardian.mobile || ""} onChange={(e) => updateGuardian(index, "mobile", e.target.value)} />
                  </Grid>
                </Grid>
              </Grid>

              {/* RIGHT: PHOTO UPLOAD FOR THIS GUARDIAN */}
              <Grid className="col-sm-3 mb-1" item xs={12} md={3}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', border: '1px solid #ddd', p: 1, borderRadius: 2, bgcolor: "#fafafa", height: "100%", minHeight: "260px" }}>
                  <Typography variant="caption" sx={{ mb: 1, fontWeight: "bold" }}>PHOTO</Typography>
                  
                  <Avatar
                    variant="rounded"
                    src={guardian.photo}
                    sx={{ width: 120, height: 140, mb: 2, border: '1px solid #eee' }}
                  >
                    {uploadingIndex === index ? <CircularProgress size={20} /> : "No Image"}
                  </Avatar>

                  <Stack spacing={1} width="100%" alignItems="center">
                    <Button
                      variant="contained"
                      component="label"
                      size="small"
                      fullWidth
                      startIcon={<CloudUploadIcon />}
                      disabled={uploadingIndex !== null}
                      sx={{ bgcolor: "teal", "&:hover": { bgcolor: "#00695c" }, maxWidth: 160 }}
                    >
                      {uploadingIndex === index ? "Uploading..." : "Upload File"}
                      <input type="file" hidden accept="image/*" onChange={(e) => handlePhotoUpload(e, index)} />
                    </Button>

                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      startIcon={<CameraAltIcon />}
                      disabled={uploadingIndex !== null}
                      onClick={() => startCamera(index)}
                      sx={{ color: "teal", borderColor: "teal", "&:hover": { borderColor: "#00695c" }, maxWidth: 160 }}
                    >
                      Capture Photo
                    </Button>
                  </Stack>

                </Box>
              </Grid>
            </Grid>
          </Box>
        ))}

        {/* 6) CONTACT PREFERENCE */}
        <Box sx={{ background: "teal", color: "#fff", p: 1, mt: 4, mb: 2 }}>
          <Typography fontWeight="bold">6) Point of Contact Preference</Typography>
        </Box>
        <Grid className="row" spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" fontWeight="bold">Mobile Preference</Typography>
            <FormControlLabel control={<Checkbox checked={form.mobilePrefMother || false} onChange={(e) => handleChange("mobilePrefMother", e.target.checked)} />} label="Mother" />
            <FormControlLabel control={<Checkbox checked={form.mobilePrefFather || false} onChange={(e) => handleChange("mobilePrefFather", e.target.checked)} />} label="Father" />
            <FormControlLabel control={<Checkbox checked={form.mobilePrefGuardian || false} onChange={(e) => handleChange("mobilePrefGuardian", e.target.checked)} />} label="Guardian" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" fontWeight="bold">Email Preference</Typography>
            <FormControlLabel control={<Checkbox checked={form.emailPrefMother || false} onChange={(e) => handleChange("emailPrefMother", e.target.checked)} />} label="Mother" />
            <FormControlLabel control={<Checkbox checked={form.emailPrefFather || false} onChange={(e) => handleChange("emailPrefFather", e.target.checked)} />} label="Father" />
            <FormControlLabel control={<Checkbox checked={form.emailPrefGuardian || false} onChange={(e) => handleChange("emailPrefGuardian", e.target.checked)} />} label="Guardian" />
          </Grid>
        </Grid>

        {/* 7) EMERGENCY CONTACT */}
        <Box sx={{ background: "teal", color: "#fff", p: 1, mt: 4, mb: 2 }}>
          <Typography fontWeight="bold">7) Emergency Contact Information</Typography>
        </Box>
        <Grid className="row" spacing={2}>
          <Grid className="col-sm-3 mb-3" item xs={12} sm={6}>
            <TextField fullWidth label="Friend/Relative Name" size="small" value={form.emergencyName || ""} onChange={(e) => handleChange("emergencyName", e.target.value)} />
          </Grid>
          <Grid className="col-sm-3 mb-3" item xs={12} sm={3}>
            <TextField fullWidth label="Mobile" size="small" value={form.emergencyMobile || ""} onChange={(e) => handleChange("emergencyMobile", e.target.value)} />
          </Grid>
          <Grid className="col-sm-3 mb-3" item xs={12} sm={3}>
            <TextField fullWidth label="Landline" size="small" value={form.emergencyLandline || ""} onChange={(e) => handleChange("emergencyLandline", e.target.value)} />
          </Grid>
          <Grid className="col-sm-3 mb-3" item xs={12} sm={6}>
            <TextField fullWidth label="Family Physician" size="small" value={form.doctorName || ""} onChange={(e) => handleChange("doctorName", e.target.value)} />
          </Grid>
          <Grid className="col-sm-3 mb-3" item xs={12} sm={3}>
            <TextField fullWidth label="Doctor Mobile" size="small" value={form.doctorMobile || ""} onChange={(e) => handleChange("doctorMobile", e.target.value)} />
          </Grid>
          <Grid className="col-sm-3 mb-3" item xs={12} sm={3}>
            <TextField fullWidth label="Doctor Landline" size="small" value={form.doctorLandline || ""} onChange={(e) => handleChange("doctorLandline", e.target.value)} />
          </Grid>
        </Grid>
      </Paper>

      {/* WEBCAM DIALOG */}
      <Dialog open={cameraOpen} onClose={stopCamera} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'teal', color: 'white' }}>
          <Typography variant="h6">Capture Guardian Photo</Typography>
          <IconButton
            aria-label="close"
            onClick={stopCamera}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: '#000' }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{ width: "100%", maxWidth: "480px", borderRadius: "8px", transform: "scaleX(-1)" }} // Mirrors the webcam for user comfort
          />
          {/* Hidden Canvas for extracting the frame */}
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", p: 2 }}>
          <Button 
            variant="contained" 
            startIcon={<CameraAltIcon />} 
            onClick={capturePhoto}
            sx={{ bgcolor: "teal", "&:hover": { bgcolor: "#00695c" }, px: 4, py: 1 }}
          >
            Snap & Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default GuardianInfo;